import consumer from "./consumer"
import { salaryOperationFailureAlertShown, markSalaryOperationFailureAlertShown } from "./salary_operation_failure_alert"
import { openMissingPfScreen } from "./pf_warning"

const terminalStatuses = ['completed', 'completed_with_errors', 'failed', 'cancelled'];
let progressIntervalId = null;
let failedItemsPage = 1;
// null until seeded from the rendered card below, so a page loaded onto an
// already terminal run (completed_with_errors stays on the sheet) never
// counts as a transition — only a run that goes active-to-terminal while
// this page watches.
let lastKnownStatus = null;

const showSalaryGenerationFailureAlert = (payrollRunId, message) => {
  if (!message || salaryOperationFailureAlertShown(payrollRunId)) return;

  markSalaryOperationFailureAlertShown(payrollRunId);
  setTimeout(() => {
    if (confirm(`Salary generation failed because ${message}, Please press OK to refresh page`)) {
      location.reload();
    }
  }, 200);
};

// Setup failing outright has no persistent UI surface once its live broadcast
// is missed: visible_on_sheet excludes a failed run entirely (unlike
// completed_with_errors, which still renders the progress card), so a page
// loaded after the fact shows an idle sheet with no explanation. The server
// keeps the latest unsuperseded failed run's outcome available separately
// from the progress card for exactly this replay — mirrors
// payroll_sheet_errors_channel.js's replayPendingPfWarning.
const replayPendingGenerationFailure = () => {
  const holder = document.getElementById('pending-generation-failure');
  if (!holder) return;

  showSalaryGenerationFailureAlert(holder.dataset.payrollRunId, holder.dataset.failureMessage);
};

const escapeHtml = (value) => $('<div>').text(value == null ? '' : value).html();
const titleize = (value) => (value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const humanize = (value) => (value || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const updateRetrySelectedButton = () => {
  $('.js-payroll-run-retry-selected').prop('disabled', $('.js-payroll-run-item-checkbox:checked').length === 0);
};

const renderFailedItemsPagination = (pagination) => {
  if (!pagination || pagination.total === 0) {
    $('.js-payroll-run-failures-summary').text('');
    $('.js-payroll-run-failed-pagination').addClass('d-none');
    return;
  }

  failedItemsPage = pagination.page;
  $('.js-payroll-run-failures-summary').text(
    `Showing ${pagination.from}-${pagination.to} of ${pagination.total} Failed Records`
  );
  $('.js-payroll-run-failed-page-summary').text(`Page ${pagination.page} of ${pagination.total_pages}`);
  $('.js-payroll-run-failed-prev').prop('disabled', pagination.page <= 1);
  $('.js-payroll-run-failed-next').prop('disabled', pagination.page >= pagination.total_pages);
  $('.js-payroll-run-failed-pagination').toggleClass('d-none', pagination.total_pages <= 1);
};

const renderFailedItems = (items) => {
  const tbody = $('.js-payroll-run-failed-items');
  tbody.empty();

  items.forEach((item) => {
    tbody.append(
      `<tr>
        <td class="text-center">
          <input type="checkbox" class="js-payroll-run-item-checkbox" value="${item.id}">
        </td>
        <td>${escapeHtml(item.employee_external_id || 'N/A')}</td>
        <td>${escapeHtml(item.employee_name || 'N/A')}</td>
        <td>${escapeHtml(item.processable_type)}</td>
        <td>${item.current_step ? escapeHtml(humanize(item.current_step)) : 'N/A'}</td>
        <td>${escapeHtml(item.error_message || '')}</td>
        <td>${escapeHtml(item.attempt_count)}</td>
      </tr>`
    );
  });

  $('.js-payroll-run-select-visible').prop('checked', false);
  updateRetrySelectedButton();
};

const renderFailedItemsPage = (data) => {
  renderFailedItems(data.failed_items || []);
  renderFailedItemsPagination(data.failed_items_pagination);
};

const updateProgressCard = (data) => {
  const card = $('.payroll-run-progress-card');
  if (!card.length) return;

  $('.js-payroll-run-status').text(titleize(data.status));
  $('.js-payroll-run-counts').text(`${data.processed}/${data.total} Processed`);
  $('.js-payroll-run-percentage').text(`${data.percentage}%`);
  $('.js-payroll-run-bar').css('width', `${data.percentage}%`).attr('aria-valuenow', data.percentage);
  $('.js-payroll-run-pending').text(data.pending);
  $('.js-payroll-run-processing').text(data.processing);
  $('.js-payroll-run-calculated').text(data.calculated);
  $('.js-payroll-run-failed').text(data.failed);

  const pendingPfWarning = data.pf_warning && !openMissingPfScreen(data.pf_warning_designations, data.id);

  if (data.status === 'failed' && !data.pf_warning) showSalaryGenerationFailureAlert(data.id, data.failure_message);

  if (data.failed > 0) {
    $('.js-payroll-run-failures').removeClass('d-none');
    renderFailedItemsPage(data);
  } else {
    $('.js-payroll-run-failures').addClass('d-none');
    renderFailedItems([]);
    renderFailedItemsPagination(null);
  }

  if (terminalStatuses.includes(data.status)) {
    if (progressIntervalId && !pendingPfWarning) {
      clearInterval(progressIntervalId);
      progressIntervalId = null;
    }
    // The sheet's own action controls (generate/lock/approve/row dropdowns)
    // were hidden server-side while this run was active and stay that way
    // until the page reloads. Not reloading automatically here, since that
    // would interrupt anyone using the retry controls this same card offers —
    // just say so, matching how salary_processing_alerts_channel.js already
    // tells the user to refresh after a legacy generation succeeds. Only for
    // an actual transition: a page loaded onto an already terminal run had
    // its controls decided correctly by the server already.
    const wasActive = lastKnownStatus !== null && !terminalStatuses.includes(lastKnownStatus);
    if (wasActive) $('.js-payroll-run-refresh-notice').removeClass('d-none');
  } else if (!progressIntervalId) {
    // A retry can reactivate a terminal run back to processing, after an
    // earlier terminal update already stopped this page's own polling
    // fallback. Without restarting it, this page would rely solely on
    // Action Cable for the retry's own eventual completion — if that
    // connection is down or misses the retry's terminal broadcast, the page
    // is stuck showing processing with nothing left to notice otherwise.
    progressIntervalId = setInterval(fetchProgress, 5000);
  }

  lastKnownStatus = data.status;
};

const fetchProgress = () => {
  const card = $('.payroll-run-progress-card');
  if (!card.length) return;

  $.get(card.data('progress-url'), updateProgressCard);
};

const fetchFailedItems = (page) => {
  const card = $('.payroll-run-progress-card');
  if (!card.length) return;

  $.get(
    card.data('failed-items-url'),
    { page: page, per_page: card.data('failed-items-per-page') },
    renderFailedItemsPage
  );
};

const retryFailedItems = (itemIds) => {
  const card = $('.payroll-run-progress-card');
  if (!card.length) return;

  $.ajax({
    type: 'POST',
    url: card.data('retry-url'),
    data: { payroll_run_item_ids: itemIds },
    success: (response) => updateProgressCard(response.progress),
    // A rejection (salary dispatched/locked, another operation active, run
    // superseded) returns 422 with the explanatory message and the current
    // progress payload — without this, the retry controls would appear to
    // silently do nothing.
    error: (xhr) => {
      const response = xhr.responseJSON;
      if (response && response.progress) updateProgressCard(response.progress);
      alert((response && response.message) || 'Unable to retry the selected items.');
    }
  });
};

jQuery(document).ready(function() {
  replayPendingGenerationFailure();

  const card = $('.payroll-run-progress-card');
  if (!card.length) return;

  // Seeded from the already-rendered card, not left null: the server only
  // renders this card while the run is active or completed_with_errors, so
  // this preserves that state as the baseline for wasActive below. Without
  // it, a run that finishes between this page rendering and the first
  // poll/cable message arriving would have its first update be terminal
  // while lastKnownStatus was still null — wasActive would read false, and
  // the refresh notice would stay hidden even though the server already
  // rendered this page with every action control removed.
  lastKnownStatus = card.data('payroll-run-status');

  consumer.subscriptions.create(
    { channel: "PayrollRunChannel", payroll_run_id: card.data('payroll-run-id') },
    { received: updateProgressCard }
  );

  fetchProgress();
  progressIntervalId = setInterval(fetchProgress, 5000);

  $('.js-payroll-run-retry-all').on('click', function() {
    const failedCount = parseInt($('.js-payroll-run-failed').text(), 10) || 0;
    if (!confirm(`Retry all ${failedCount} failed records?`)) return;

    retryFailedItems([]);
  });

  $('.js-payroll-run-retry-selected').on('click', function() {
    const itemIds = $('.js-payroll-run-item-checkbox:checked').map(function() {
      return $(this).val();
    }).get();
    if (!itemIds.length) return;

    retryFailedItems(itemIds);
  });

  $(document).on('change', '.js-payroll-run-select-visible', function() {
    $('.js-payroll-run-item-checkbox').prop('checked', $(this).is(':checked'));
    updateRetrySelectedButton();
  });

  $(document).on('change', '.js-payroll-run-item-checkbox', function() {
    const visibleCheckboxes = $('.js-payroll-run-item-checkbox');
    const checkedCheckboxes = $('.js-payroll-run-item-checkbox:checked');
    $('.js-payroll-run-select-visible').prop(
      'checked',
      visibleCheckboxes.length > 0 && visibleCheckboxes.length === checkedCheckboxes.length
    );
    updateRetrySelectedButton();
  });

  $('.js-payroll-run-failed-prev').on('click', function() {
    if (failedItemsPage <= 1) return;

    fetchFailedItems(failedItemsPage - 1);
  });

  $('.js-payroll-run-failed-next').on('click', function() {
    fetchFailedItems(failedItemsPage + 1);
  });
});
